import { PhoneEnabledSharp, SmartphoneSharp } from '@mui/icons-material'
import { IconButton, InputAdornment, TextField, Tooltip } from '@mui/material'
import React from 'react'
import { formatPhoneNumberIntl } from 'react-phone-number-input'

import { PhoneNumberInput } from '../Input/components'

interface PhoneFieldWithCallProps {
  value?: string
  onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
  onCall?: () => void
  disabled?: boolean
  label?: string
  placeholder?: string
  fullWidth?: boolean
  variant?: 'phone' | 'mobile'
  loading?: boolean
  sx?: any
  name?: string
  error?: any
  helperText?: string
  callViaVoip?: boolean
  [key: string]: any // For additional props
}

const PhoneFieldWithCall = React.forwardRef<HTMLDivElement, PhoneFieldWithCallProps>(
  (
    {
      value = '',
      onChange,
      onCall,
      disabled = false,
      label,
      placeholder,
      fullWidth = true,
      variant = 'phone',
      loading = false,
      sx,
      name,
      error,
      helperText,
      callViaVoip,
      ...props
    },
    ref
  ) => {
    const hasValue = value && value.trim().length > 0
    const isCallDisabled = !hasValue || loading

    const handleCallClick = (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()

      if (isCallDisabled) return

      if (callViaVoip) {
        window.open(`tel:${value}`, '_self')
      } else if (onCall) {
        onCall()
      }
    }

    const CallIcon = variant === 'mobile' ? SmartphoneSharp : PhoneEnabledSharp

    return (
      <Tooltip
        title={value ? formatPhoneNumberIntl(value) : ''}
        placement="top"
        slotProps={{
          tooltip: {
            style: {
              fontSize: '72px',
              letterSpacing: '0.1em',
              maxWidth: 'none',
              width: 'fit-content'
            }
          }
        }}
        disableInteractive
        disableTouchListener
      >
        <TextField
          ref={ref}
          variant="outlined"
          disabled={disabled}
          label={label}
          name={name}
          onChange={onChange}
          value={value}
          fullWidth={fullWidth}
          placeholder={placeholder}
          error={!!error}
          helperText={helperText}
          size="small"
          sx={{
            '& .PhoneInput': { display: 'flex' },
            '& .PhoneInputInput': {
              border: 'none',
              outline: 'none',
              fontFamily: 'fontFamily',
              font: 'inherit',
              color: 'currentColor'
            },
            ...sx
          }}
          slotProps={{
            input: {
              inputComponent: PhoneNumberInput,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleCallClick}
                    disabled={isCallDisabled}
                    size="small"
                    sx={{
                      opacity: isCallDisabled ? 0.3 : 1,
                      transition: 'opacity 0.2s ease',
                      '&:hover': {
                        backgroundColor: isCallDisabled ? 'transparent' : 'action.hover'
                      }
                    }}
                    aria-label={variant === 'mobile' ? 'Call mobile number' : 'Call phone number'}
                  >
                    <CallIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }
          }}
          {...props}
        />
      </Tooltip>
    )
  }
)

PhoneFieldWithCall.displayName = 'PhoneFieldWithCall'

export default PhoneFieldWithCall
